import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { Select } from 'antd';
import { useConfig } from '@ohpen/react-utils';

import styles from './styles.module.css';

/** Where the previewed client is kept, so a reload and every screen agree on it. */
const STORAGE_KEY = 'dev-theme';

export interface DevClient {
  /** The client whose theme and logo to load. */
  selectedClient: string;
  /** The picker, or undefined outside dev. */
  clientSelect?: ReactElement;
}

/**
 * Lets a developer look at the portal with another client's theme. Only in dev, and only
 * for the clients the deployment lists.
 */
export const useDevClient = (): DevClient => {
  const { CLIENT: client, STAGE: stage, DEVELOPMENT_OVERRIDES } = useConfig();
  const [selectedClient, setSelectedClient] = useState<string>(client);

  useEffect(() => {
    if (stage === 'dev') {
      setSelectedClient(localStorage.getItem(STORAGE_KEY) ?? client);
    }
  }, [client, stage]);

  const onChange = useCallback((value: string) => {
    setSelectedClient(value);
    localStorage.setItem(STORAGE_KEY, value);
  }, []);

  const clientSelect = useMemo(() => {
    const clientsList = DEVELOPMENT_OVERRIDES?.CLIENTS_LIST;
    if (!clientsList || stage !== 'dev') return undefined;

    return (
      <Select
        className={styles.clientSelect}
        value={selectedClient}
        onChange={onChange}
        options={clientsList.map((option: string) => ({ value: option, label: option }))}
        variant="borderless"
      />
    );
  }, [DEVELOPMENT_OVERRIDES?.CLIENTS_LIST, onChange, selectedClient, stage]);

  return { selectedClient, clientSelect };
};
